// @ts-check
/**
 * Topics — what both ends of a channel share: the grammar of a topic, the
 * merge a burst coalesces by, and the two defaults.
 *
 * Carried from the ConRes 2026 tree's `helpers/channels.js`, whose
 * `openHub()` is `hub.js`'s here with the entrypoint URL required rather
 * than derived. Design: that tree's
 * `documentation/2026-08-20-Pub-Sub-Using-Async-Atomics.md` §9.
 *
 * **Topics are strings the application chooses.** A topic is `/`-separated
 * (`usage`, `segments/<key>`); a pattern is a topic, or a topic whose LAST
 * segment is `*`, which stands for one or more further segments. `*` alone
 * matches everything.
 *
 * @module @smotaal/platform-orchestration/lib/channels/topics
 * @author Saleh Abdel Motaal <dev@smotaal.io>
 * @ai Claude Fable 5.1 (code generation)
 */

/**
 * A topic pattern: exact, or a trailing `*` segment wildcard.
 *
 * @typedef {string} Topic
 */

/**
 * @template [T=unknown]
 * @typedef {{
 *   topic: string,
 *   data: T,
 *   origin: string | null,
 *   at: number,
 * }} Publication `origin` is the publishing endpoint's name; `null` when a
 *   coalesced publication came from more than one. `at` is the publisher's
 *   clock, for ordering only.
 */

/** The default coalescing window, in milliseconds. */
export const DEFAULT_COALESCE_MILLISECONDS = 250;

/** The `name` of the shared hub — one per origin, whoever opens it. */
export const DEFAULT_HUB_NAME = 'channels';

/**
 * Refuses a topic or pattern that is not one: empty, a `/`-leading or
 * trailing string, an empty segment, or a `*` anywhere but as the whole last
 * segment.
 *
 * @param {string} topic
 * @param {{ pattern?: boolean }} [options] `pattern` admits a trailing `*`.
 * @returns {string}
 */
export function assertTopic(topic, { pattern = false } = {}) {
    const text = `${topic ?? ''}`;
    if (!text) throw new TypeError('A topic cannot be empty');
    const segments = text.split('/');
    for (const [index, segment] of segments.entries()) {
        if (!segment) throw new TypeError(`Not a topic: “${text}” — empty segment`);
        if (segment.includes('*')) {
            const last = index === segments.length - 1;
            if (!pattern || !last || segment !== '*') {
                throw new TypeError(
                    `Not a ${pattern ? 'pattern' : 'topic'}: “${text}” — ` +
                    (pattern ? '“*” may only be the whole last segment' : 'a topic carries no “*”'));
            }
        }
    }
    return text;
}

/**
 * Whether a pattern matches a topic. `segments/*` matches `segments/a` and
 * `segments/a/b`, never `segments`; `*` matches everything.
 *
 * @param {string} pattern
 * @param {string} topic
 */
export function matchesTopic(pattern, topic) {
    if (pattern === topic) return true;
    if (pattern === '*') return true;
    if (!pattern.endsWith('/*')) return false;
    const prefix = pattern.slice(0, -1);
    return topic.startsWith(prefix) && topic.length > prefix.length;
}

/** @param {any} value */
const isPlainObject = value =>
    value !== null && typeof value === 'object' && !Array.isArray(value)
    && (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null);

/**
 * The default merge of two publications on one topic within a window: array
 * fields unioned in first-seen order, every other field the latest, the
 * origin kept only when both agree.
 *
 * @param {Publication<any>} previous
 * @param {Publication<any>} next
 * @returns {Publication<any>}
 */
export function mergePublications(previous, next) {
    let data = next.data;
    if (isPlainObject(previous.data) && isPlainObject(next.data)) {
        data = { ...previous.data };
        for (const [key, value] of Object.entries(next.data)) {
            const before = data[key];
            data[key] = Array.isArray(before) && Array.isArray(value)
                ? [...new Set([...before, ...value])]
                : value;
        }
    }
    return {
        topic: next.topic,
        data,
        origin: previous.origin === next.origin ? next.origin : null,
        at: next.at,
    };
}

/**
 * Patterns as the hub keeps them: an array of asserted patterns, or none.
 *
 * @param {unknown} topics
 */
export function normaliseTopics(topics) {
    if (!Array.isArray(topics)) return [];
    return topics.map(topic => assertTopic(`${topic}`, { pattern: true }));
}
